import { TrackerDevice } from 'cloudsort-client';
import { LatLngMapboxAlike } from './types';
import moment from 'moment';
import { mapColors } from './constants';

//Calculate color having two colors and percentage -- HEX values
const interpolateColor = (
  color1: string,
  color2: string,
  percentage: number,
) => {
  const color1Map = color1
    .slice(1)
    .match(/.{1,2}/g)
    ?.map((oct) => parseInt(oct, 16) * (1 - percentage));
  const color2Map = color2
    .slice(1)
    .match(/.{1,2}/g)
    ?.map((oct) => parseInt(oct, 16) * percentage);
  let ci = [0, 1, 2].map((i) =>
    Math.min(Math.round(color1Map![i]! + color2Map![i]), 255),
  );
  return ci
    .reduce((a, v) => (a << 8) + v, 0)
    .toString(16)
    .padStart(6, '0');
};

const renderBatteryPercentageLabel = (device?: TrackerDevice) => {
  if (device?.battery_exact)
    return `${(Number(device.battery_exact) * 100).toFixed()}%`;

  if (device?.battery_range_lower && device?.battery_range_upper)
    return `${(
      Number(device.battery_range_lower) * 100
    ).toFixed()}-${(
      Number(device.battery_range_upper) * 100
    ).toFixed()}%`;

  return 'N/A';
};

const getPingColorConditions = () => {
  const pingColors = Array.from({ length: 10 }).map((_, i) =>
    interpolateColor(
      mapColors.gradient.end,
      mapColors.gradient.start,
      i / 10,
    ),
  );

  return [
    {
      after: moment().subtract(15, 'minutes'),
      color: pingColors[0],
    },
    {
      after: moment().subtract(30, 'minutes'),
      color: pingColors[1],
    },
    {
      after: moment().subtract(1, 'hour'),
      color: pingColors[2],
    },
    {
      after: moment().subtract(2, 'hours'),
      color: pingColors[3],
    },
    {
      after: moment().subtract(4, 'hours'),
      color: pingColors[4],
    },
    {
      after: moment().subtract(8, 'hours'),
      color: pingColors[5],
    },
    {
      after: moment().subtract(12, 'hours'),
      color: pingColors[6],
    },
    {
      after: moment().subtract(1, 'day'),
      color: pingColors[7],
    },
    {
      after: moment().subtract(2, 'days'),
      color: pingColors[8],
    },
    {
      after: moment('2000-01-01'),
      color: pingColors[9],
    },
  ];
};

const renderTemperatureLabel = (device?: TrackerDevice) => {
  const celsiusToFahrenheit = (celsius: number) => {
    return celsius * 1.8 + 32;
  };

  const toOneDecimal = (num: number) => {
    return Number(num).toFixed(1);
  };

  if (device?.temperature_exact)
    return `${toOneDecimal(
      celsiusToFahrenheit(device.temperature_exact),
    )} ºF / ${toOneDecimal(device.temperature_exact)} ºC`;

  if (
    device?.temperature_range_lower &&
    device?.temperature_range_upper
  )
    return `${toOneDecimal(
      celsiusToFahrenheit(device.temperature_range_lower),
    )} - ${toOneDecimal(
      celsiusToFahrenheit(device.temperature_range_upper),
    )} ºF / ${toOneDecimal(
      device.temperature_range_lower,
    )} - ${toOneDecimal(device.temperature_range_upper)} ºC`;

  return 'N/A';
};

const renderLocalTimestamp = (dateTime: string) => {
  return new Date(dateTime).toLocaleString();
};

const renderLocalTimezone = () => {
  const offset = new Date().getTimezoneOffset();

  const offsetHours = -offset / 60;

  if (offsetHours === 0) return 'UTC';

  return `UTC${offsetHours > 0 ? '+' : '-'}${offsetHours}`;
};

const getNormalizedBounds = (
  bounds: LatLngMapboxAlike,
): LatLngMapboxAlike => {
  const normalizeLng = (lng: number) =>
    lng < -180 ? -180 : lng > 180 ? 180 : lng;

  const normalizeLat = (lat: number) =>
    lat < -90 ? -90 : lat > 90 ? 90 : lat;

  return {
    _sw: {
      lng: normalizeLng(bounds._sw.lng),
      lat: normalizeLat(bounds._sw.lat),
    },
    _ne: {
      lng: normalizeLng(bounds._ne.lng),
      lat: normalizeLat(bounds._ne.lat),
    },
  };
};

export {
  interpolateColor,
  renderBatteryPercentageLabel,
  renderTemperatureLabel,
  renderLocalTimestamp,
  renderLocalTimezone,
  getNormalizedBounds,
  getPingColorConditions,
};
